import type { CityDto } from '@/core/data/location/location.interface';
import type { SparkIntent } from '@/core/data/spark/spark.interface';
import { addDays, format } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';

export const RESULTS_CUTOFF_HOUR = 22;
const FALLBACK_TIMEZONE = 'UTC';

const isValidTimezone = (timezone?: string | null): timezone is string => {
  if (!timezone) {
    return false;
  }

  try {
    formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
    return true;
  } catch {
    return false;
  }
};

interface ResultsReferenceTimezoneInput {
  activeIntent?: SparkIntent | null;
  earliestEnabledIntent?: SparkIntent | null;
  city?: CityDto | null;
  browserTimezone?: string | null;
  fallbackTimezone?: string;
}

export const getResultsReferenceTimezone = ({
  activeIntent,
  earliestEnabledIntent,
  city,
  browserTimezone,
  fallbackTimezone = FALLBACK_TIMEZONE,
}: ResultsReferenceTimezoneInput): string => {
  const candidates = [
    activeIntent?.timeZone,
    earliestEnabledIntent?.timeZone,
    city?.timezone,
    browserTimezone,
    fallbackTimezone,
  ];

  return candidates.find((candidateTimezone) => isValidTimezone(candidateTimezone)) || FALLBACK_TIMEZONE;
};

const getZonedHour = (now: Date, timezone: string): number => {
  return Number(formatInTimeZone(now, timezone, 'H'));
};

const getZonedDateKey = (now: Date, timezone: string): string => {
  return formatInTimeZone(now, timezone, 'yyyy-MM-dd');
};

export const getTodayDateKey = (now: Date, timezone: string): string => {
  const resolvedTimezone = isValidTimezone(timezone) ? timezone : FALLBACK_TIMEZONE;
  return getZonedDateKey(now, resolvedTimezone);
};

export const getDefaultResultsDateKey = (now: Date, timezone: string, cutoffHour = RESULTS_CUTOFF_HOUR): string => {
  const resolvedTimezone = isValidTimezone(timezone) ? timezone : FALLBACK_TIMEZONE;
  const zonedHour = getZonedHour(now, resolvedTimezone);

  if (zonedHour < cutoffHour) {
    return getZonedDateKey(now, resolvedTimezone);
  }

  const zonedNow = toZonedTime(now, resolvedTimezone);
  return format(addDays(zonedNow, 1), 'yyyy-MM-dd');
};

export const isIntentHiddenByCutoff = (intent: SparkIntent, now: Date, cutoffHour = RESULTS_CUTOFF_HOUR): boolean => {
  const resolvedTimezone = isValidTimezone(intent.timeZone) ? intent.timeZone : FALLBACK_TIMEZONE;
  const todayDateKey = getZonedDateKey(now, resolvedTimezone);
  const zonedHour = getZonedHour(now, resolvedTimezone);
  const intentDateKey = String(intent.date).slice(0, 10);

  return intentDateKey === todayDateKey && zonedHour >= cutoffHour;
};
